import { QuickPreviewModal } from '@/components/react/ui/modals/QuickPreviewModal';
import { ReactBadge } from '@/components/react/ui/ReactBadge';
import { ReactCta } from '@/components/react/ui/ReactCta';
import { ReactHeading } from '@/components/react/ui/ReactHeading';
import { ReactImage } from '@/components/react/ui/ReactImage';
import { ReactModalTriggerCta } from '@/components/react/ui/ReactModalTrigger';
import { cx } from '@/cva.config';
import type { ImageMetadata } from 'astro';
import type { ComponentProps } from 'react';

export interface ProductCardProps {
  alt?: string;
  badges?: ComponentProps<typeof ReactBadge>[];
  className?: string;
  cta?: ComponentProps<typeof ReactCta>
    & Required<Pick<ComponentProps<typeof ReactCta>, 'href'>>;
  eyebrow?: string;
  heading?: ComponentProps<typeof ReactHeading>;
  href?: string; // fallback if cta.href is not provided
  image: ImageMetadata;
  price?: string;
  promo?: string;
  quickViewModal?: ComponentProps<typeof QuickPreviewModal>;
  rootClassName?: string;
  text?: string;
  modalTriggerCta?: ComponentProps<typeof ReactCta>;
  ctaAlignment?: 'bottom' | 'auto';
}

export const ReactProductCard = ({
  alt,
  badges,
  className,
  cta,
  eyebrow,
  heading,
  href: fallbackHref,
  image,
  price,
  promo,
  quickViewModal,
  rootClassName,
  text,
  modalTriggerCta,
  ctaAlignment = 'bottom',
}: ProductCardProps) => {
  const href = cta?.href ?? fallbackHref;

  return (
    <div
      className={cx(
        'group relative flex h-full w-full flex-col gap-4',
        rootClassName,
      )}
    >
      <div
        className={cx(
          'group relative flex w-full flex-col gap-4 rounded-lg',
          className,
        )}
      >
        <a
          className={cx(
            'group relative flex w-full flex-col overflow-clip rounded-lg no-underline',
          )}
          href={href}
        >
          <ReactImage
            alt={alt}
            {...image}
            className="absolute inset-0 h-full w-full object-cover"
          />

          <div
            className="flex aspect-square flex-col items-center justify-between bg-cover bg-center bg-no-repeat p-3"
            style={{ backgroundImage: `url(${image.src})` }}
          >
            {badges && (
              <div className="z-10 flex w-full flex-wrap items-start gap-3">
                {badges.map((badge, idx) => (
                  <ReactBadge key={idx} {...badge} />
                ))}
              </div>
            )}
          </div>
        </a>
        {quickViewModal && modalTriggerCta && (
          <ReactModalTriggerCta
            className="bg-blurb-bg-surface hover-always:opacity-100 hover-always:bg-blurb-bg-surface absolute bottom-5 left-1/2 -translate-x-1/2 opacity-0 transition-opacity duration-250 ease-in group-focus-within:opacity-100 group-hover:opacity-100"
            cta={modalTriggerCta}
          >
            <QuickPreviewModal {...quickViewModal} />
          </ReactModalTriggerCta>
        )}
      </div>
      <div
        className={cx('flex h-full flex-col gap-4 px-0 pb-1 md:px-3', {
          'justify-between': ctaAlignment === 'bottom',
          'justify-start': ctaAlignment === 'auto',
        })}
      >
        <div className="flex flex-col justify-start gap-2">
          {eyebrow && (
            <div className="text-blurb-text-subtle text-sm">{eyebrow}</div>
          )}
          {heading && !href && <ReactHeading {...heading} />}
          {heading && !!href && (
            <a
              href={href}
              className="text-blurb-text-subtle no-underline hover:underline active:underline"
            >
              <ReactHeading {...heading} />
            </a>
          )}
          {text && (
            <div className="text-blurb-text-subtle text-base">{text}</div>
          )}
          {price && (
            <div className="text-blurb-text-subtle font-bold">{price}</div>
          )}
          {promo && (
            <div className="text-blurb-text-accent-red font-bold">{promo}</div>
          )}
        </div>
        {cta && <ReactCta {...cta} />}
      </div>
    </div>
  );
};
